import { Box, Card, CardContent, Skeleton, Stack } from "@mui/material";

const DESKTOP_GRID_COLS =
  "minmax(0, 2fr) minmax(0, 1.15fr) 88px 104px 148px 168px";

const DESKTOP_COL_GAP = 1.5;

const listContainerSx = {
  display: "grid",
  gap: 1.5,
  gridTemplateColumns: "1fr",
  "@media (min-width:572px) and (max-width:1199.95px)": {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
} as const;

function CompactCardSkeleton() {
  return (
    <Box sx={{ display: { xs: "flex", lg: "none" }, flexDirection: "column", gap: 1.25 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Skeleton variant="text" width="70%" height={22} />
        <Skeleton variant="rounded" width={64} height={22} />
      </Stack>
      <Skeleton variant="rounded" width="100%" height={6} sx={{ borderRadius: 999 }} />
      <Stack direction="row" spacing={1.5}>
        <Skeleton variant="text" width={28} height={14} />
        <Skeleton variant="text" width={28} height={14} />
        <Skeleton variant="text" width={28} height={14} />
      </Stack>
      <Skeleton variant="text" width="55%" height={16} />
      <Stack direction="row" spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="circular" width={28} height={28} />
          <Box>
            <Skeleton variant="text" width={48} height={12} />
            <Skeleton variant="text" width={72} height={14} />
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="circular" width={28} height={28} />
          <Box>
            <Skeleton variant="text" width={48} height={12} />
            <Skeleton variant="text" width={72} height={14} />
          </Box>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1}>
        <Skeleton variant="rounded" height={34} sx={{ borderRadius: "8px", flex: 1 }} />
        <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: "8px" }} />
      </Stack>
    </Box>
  );
}

function DesktopRowSkeleton() {
  return (
    <Box
      sx={{
        display: { xs: "none", lg: "grid" },
        gridTemplateColumns: DESKTOP_GRID_COLS,
        gap: DESKTOP_COL_GAP,
        alignItems: "center",
      }}
    >
      <Box>
        <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1 }} />
        <Stack direction="row" spacing={2}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
        </Stack>
      </Box>
      <Box>
        <Skeleton variant="text" width={72} height={16} sx={{ mb: 0.5 }} />
        <Skeleton variant="rounded" width="100%" height={6} sx={{ mb: 1, borderRadius: 999 }} />
      </Box>
      <Skeleton variant="rounded" width={72} height={22} />
      <Skeleton variant="rounded" width={64} height={20} />
      <Box>
        <Skeleton variant="text" width={96} height={16} />
        <Skeleton variant="text" width={72} height={12} />
      </Box>
      <Stack direction="row" spacing={1}>
        <Skeleton variant="rounded" width={110} height={32} sx={{ borderRadius: "8px" }} />
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: "8px" }} />
      </Stack>
    </Box>
  );
}

function InspectionOrderRowSkeleton() {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        height: "100%",
      }}
    >
      <CardContent sx={{ p: { xs: "14px !important", lg: "16px !important" } }}>
        <CompactCardSkeleton />
        <DesktopRowSkeleton />
      </CardContent>
    </Card>
  );
}

export default function InspectionOrdersListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <Box>
      <Box
        sx={{
          display: { xs: "none", lg: "grid" },
          gridTemplateColumns: DESKTOP_GRID_COLS,
          gap: DESKTOP_COL_GAP,
          border: "1px solid transparent",
          px: "16px",
          pb: 1,
          boxSizing: "border-box",
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="text" width={80} height={14} />
        ))}
      </Box>
      <Box sx={listContainerSx}>
        {Array.from({ length: count }).map((_, i) => (
          <InspectionOrderRowSkeleton key={i} />
        ))}
      </Box>
    </Box>
  );
}
